import React, { useEffect, useCallback, useState } from "react";
import moment from "moment";
import { Config } from "@eagerdog/config";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';

import { Constants } from "@eagerdog/constants";
import { ICourseSummary, ICourseRegistrationResponse, IAddress, CourseRegistrationCreateDto, ISession, ICourseDogSessions, DOG_VACCINE_TYPES } from "@eagerdog/interfaces";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services";
import { AxiosError } from "axios";

import RegisterSteps, { IStep } from "./RegisterSteps/RegisterSteps";
import CourseCart from "./CourseCart/CourseCart";
import DogSelection, { ISelectedDog } from "./DogSelection/DogSelection";

import { Modal, useModal } from "src/components/Modal/Modal";
import DogForm from "src/components/StepForm/DogForm/DogForm";
import { toast } from "src/components/Toast/ToastManager";
import Breadcrumb, { ICrumb } from "src/components/Breadcrumb/Breadcrumb";
import Button from "src/components/Button/Button";
import Icon from "src/components/Icon/Icon";
import Dropdown, { IOption }  from "src/components/Dropdown/Dropdown";
import Input from "src/components/Input/Input";
import Chip from "src/components/Chip/Chip";

import Checkout from "src/routes/EventRegister/Checkout/Checkout";
import { usePointsReminder } from "src/components/PointsReminder/usePointsReminder";
import { pointsReminderEnabled } from "src/components/PointsReminder/pointsReminder";
import UserAddress from "src/routes/EventRegister/UserAddress/UserAddress";

import styles from "./CourseRegister.module.scss";
import Checkbox from "src/components/Checkbox/Checkbox";
import DogPortrait from "src/components/DogPortrait/DogPortrait";

interface IProps {
    cid?: string;
}

const CourseRegister: React.FC<IProps> = (props) => {
  const { cid } = useParams<{ cid: string }>();
  const user = helperService.getUser();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use ref for ban status to ensure it's immediately available in event handlers
  const isUserBannedRef = React.useRef<boolean>(false);

  const [onBehalf, setOnBehalf] = useState<string>();
  const [onBehalfEmail, setOnBehalfEmail] = useState<string>();
  const [onBehalfName, setOnBehalfName] = useState<string>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_onBehalfJunior, setOnBehalfJunior] = useState<string>();
  const [isClaimed, setIsClaimed] = useState<boolean>(false);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [showSchedule, setShowSchedule] = useState<boolean>(false);
  const [dogs, setDogs] = useState<ISelectedDog[]>([]);
  const [cart, setCart] = useState<any>(null);
  const [seminarSessions, setSeminarSessions] = useState<string[]>([]); // Track selected sessions for seminars

  const [loading, setLoading] = useState<boolean>(true);

  const [userAddress, setUserAddress] = useState<IAddress>();
  const [userPhone, setUserPhone] = useState<string>();

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>();
  const [stripeIntent, setStripeIntent] = useState<ICourseRegistrationResponse | null>();
  const [completingFree, setCompletingFree] = useState<boolean>(false);
  const [lastErrorShown, setLastErrorShown] = useState<string>('');

  const [pointsToRedeem, setPointsToRedeem] = useState<number>(0);
  const [offerId, setOfferId] = useState<string>("");
  const [offerApplied, setOfferApplied] = useState(false);

  const [crumbs, setCrumbs] = useState<ICrumb[]>([
      { label: "Courses", path: "/courses" },
      { label: "..." }
  ]);

  const [steps, setSteps] = useState<IStep[]>([
      { label: "Select Dogs" },
      { label: "Select Sessions" },
      { label: "Payment" }
  ]);

  const [agreeToWaiver, setAgreeToWaiver] = useState<boolean>(false);
  const [agreeToClubPolicies, setAgreeToClubPolicies] = useState<boolean>(false);

  const [course, setCourse] = useState<ICourseSummary | null>(null);
  //const [isAdminUser, setIsAdminUser] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isAdminUser, setIsAdminUser] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isUserBanned, setIsUserBanned] = useState<boolean>(false); // State kept for potential future use, ref is used for immediate access

  const { isShown, toggle } = useModal();
  const [modal, setModal] = useState<string>();

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: course?.currency?.toUpperCase() || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Helper function to get session label with price for classes and seminars
  const getSessionLabel = (session: ISession) => {
    const dateLabel = moment(session.start_date).format("dddd, MMM D");
    
    // Only show price for classes (drop-in) and seminars with per-session pricing
    if (course && (course.course_type === Constants.course_type.drop_in || course.course_type === Constants.course_type.seminar)) {
      // Check if course has session fees (per-session pricing)
      if (session.fee !== undefined && session.fee !== null) {
        return `${dateLabel} - ${formatter.format(session.fee)}`;
      }
    }
    
    return dateLabel;
  };

  const onBehalfPaymentMethods: IOption[] = [
    { value: "Cheque", id: Constants.payment_method_type.cheque },
    { value: "Cash", id: Constants.payment_method_type.cash },
    { value: "E-Transfer", id: Constants.payment_method_type.etransfer },
    { value: "Paypal", id: Constants.payment_method_type.paypal },
    { value: "Other", id: Constants.payment_method_type.other }
  ];

  const [onBehalfPaymentMethod, setOnBehalfPaymentMethod] = useState<IOption>({ value: "Cheque", id: Constants.payment_method_type.cheque });
  const [chequeNumber, setChequeNumber] = useState<string | undefined>(undefined);

  const [newDogAdded, setNewDogAdded] = useState<boolean>(false);

  const paymentStepIndex = steps.length - 1;
  const hasWaiverCourse = !!(course?.waiver && course.waiver.trim() !== '');
  const hasClubPoliciesCourse = !!(course?.club_policies && course.club_policies.trim() !== '');
  const clubPoliciesStepIndex = hasClubPoliciesCourse ? paymentStepIndex - 1 : -1;
  const waiverStepIndex = hasWaiverCourse ? (hasClubPoliciesCourse ? paymentStepIndex - 2 : paymentStepIndex - 1) : -1;

  const checkDogVaccineRequirements = (dog: ISelectedDog): { passed: boolean; missingLabels: string[] } => {
    const reqs = course?.vaccine_requirements;
    if (!reqs?.enabled || !reqs.required_types?.length) return { passed: true, missingLabels: [] };

    const today = new Date();
    const missingLabels = reqs.required_types
      .filter((reqType: string) => {
        return !(dog.attachments ?? []).some(att =>
          att.type === reqType && new Date(att.expiry_date) >= today
        );
      })
      .map((reqType: string) => DOG_VACCINE_TYPES.find(t => t.value === reqType)?.label ?? reqType);

    return { passed: missingLabels.length === 0, missingLabels };
  };

  const validStep = (step: number) => {
    if (waiverStepIndex !== -1 && step === waiverStepIndex) {
        if (!agreeToWaiver) {
            toast.show({
                title: "Course Registration",
                content: "Please read and agree to the course waiver to continue.",
                duration: 5000,
                type: "fail"
            });
            return false;
        }
        return true;
    }
    if (clubPoliciesStepIndex !== -1 && step === clubPoliciesStepIndex) {
        if (!agreeToClubPolicies) {
            toast.show({
                title: "Course Registration",
                content: "Please read and agree to the club policies to continue.",
                duration: 5000,
                type: "fail"
            });
            return false;
        }
        return true;
    }

    // For seminars, step 0 is "Select Sessions", step 1 is "Payment"
    // For courses, step 0 is "Select Dogs", step 1 is "Select Sessions" (drop-in) or "Payment" (full)
    
    const isSeminar = course?.course_type === Constants.course_type.seminar;
    
    switch (step) {
        case 0:
            // For seminars, step 0 is session selection (always valid, handled in step 1 for drop-in)
            // For courses, step 0 is dog selection
            if (isSeminar) {
                return true; // Seminars don't need dog validation
            }
            
            if (dogs.filter(dog => dog.selected).length === 0) {
                toast.show({
                    title: "Course Registration Error",
                    content: "Please select atleast one dog",
                    duration: 5000,
                    type: "fail"
                });

                return false;
            } else {
                return true;
            }
        case 1:
            // For seminars on drop-in, this would be payment (no validation needed)
            // For drop-in courses, this is session selection validation
            if (isSeminar) {
                return true; // Seminars with drop-in will validate sessions if needed
            }
            
            let allDogsHaveSessions:boolean = true;

            dogs.filter(dog => dog.selected).forEach((dog: ISelectedDog, index: number) => {
                if (dog.sessions[0]?.session_ids?.length === 0) {
                    allDogsHaveSessions = false;
                }
            });

            if (!allDogsHaveSessions) {
                toast.show({
                    title: "Course Registration Error",
                    content: "Please select atleast one session for all dogs",
                    duration: 5000,
                    type: "fail"
                });
            }

            return allDogsHaveSessions;
    }

    return true;
  }

  const getModalContent = () => {
    switch (modal) {
      case "addDog":
        return <DogForm
            onBehalf={onBehalf}
            onBehalfEmail={onBehalfEmail}
            clubId={course?.club._id}
            onFinish={() => {
                    toggle();
                    setNewDogAdded(true);
                    setLoading(true);
                }
            }
        />;
    }

    return <></>;
  }

  const getDogs = useCallback((_cid?: string, _uid?: string) => {
    if (_cid && _uid) {
        apiService.getUserDogs(_uid).then((dogResponse) => {
            if (_cid) {
                apiService.getUserDogs(_uid, _cid).then((clubResponse) => {
                    const combined = [
                        ...dogResponse.map(dog => ({ ...dog, selected: false, sessions: [] as ICourseDogSessions[] })),
                        ...clubResponse.map(dog => ({ ...dog, selected: false, sessions: [] as ICourseDogSessions[] }))
                    ];
                    // Auto-select the last dog in the combined list (the newly added dog)
                    if (newDogAdded && combined.length > 0) {
                        const newDog = combined[combined.length - 1];
                        newDog.selected = true;
                        newDog.sessions = [{
                            dog_id: newDog._id,
                            session_ids: (course?.schedule || []).map((session: ISession) => String(session._id)).filter(Boolean)
                        }];
                    }
                    setDogs(combined);
                    setNewDogAdded(false);
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    } else {
        apiService.getUserDogs(user._id).then((dogResponse) => {
            if (dogResponse.length > 0) {
                setDogs(dogResponse.map((dog, index) => ({ ...dog, sessions: [], selected: (newDogAdded && index === dogResponse.length - 1) ? true : false })));
                setNewDogAdded(false);
            }
        }).finally(() => {
            setLoading(false);
        });
    }
  }, [newDogAdded, user._id, course]);

  const isOnBehalfOf = () => {
    if (onBehalf && onBehalfEmail) {
        return true;
    }

    return false;
  }

  const registerOnBehalf = () => {
    if (course) {
        const courseRegistrations = course.course_type === Constants.course_type.seminar
            ? [{ session_ids: seminarSessions }] // For seminars, use seminar sessions
            : dogs.filter(dog => dog.selected).map(dog => ({
                dog_id: dog._id,
                session_ids: dog.sessions[0].session_ids || []
              }));

        let payload: CourseRegistrationCreateDto = {
            course_registrations: courseRegistrations,
            payment_method_type: onBehalfPaymentMethod.id,
            registration_kind: Constants.registration_kind.club,
            cheque_number: chequeNumber,
            owner_id: onBehalf ?? user._id,
            club_points_to_redeem: pointsToRedeem || undefined,
            course_waitlist_id: offerId || undefined
        };

        apiService.createCourseRegistrationByClubManager(course?._id, payload).then((response) => {
            if (response._id === undefined && course) {
                toast.show({
                    title: "Course Registration",
                    content: "You've successfully registered " + onBehalfEmail +" to your course.",
                    duration: 5000,
                    type: "fail"
                });

                navigate("/c/" + course.handle);
            }
        });
    }
  }

  const getSessionCount = () => {
    return course?.schedule?.reduce((total, session) => total + (session.regular_entries || 0), 0) || 0;
  }

  const getMaxDogCount = () => {
    return course?.schedule.reduce((total, session) => total + (session.regular_capacity || 0), 0) || 0;
  }

  const sessionIdOf = (value: unknown): string =>
    value != null && value !== "" ? String(value) : "";

  const scheduleSessionIds = (): string[] =>
    (course?.schedule || []).map((session) => sessionIdOf(session._id)).filter(Boolean);

  const dogSessionIds = (dog: ISelectedDog): string[] =>
    (dog.sessions[0]?.session_ids || []).map(sessionIdOf).filter(Boolean);

  const dogHasSession = (dog: ISelectedDog, sessionId: string): boolean =>
    dogSessionIds(dog).includes(sessionIdOf(sessionId));

  const toggleSessionFromDog = (_dogId: string, sessionId: string) => {
    const id = sessionIdOf(sessionId);
    if (!id) return;

    setDogs(dogs.map(dog => {
        if (sessionIdOf(dog._id) !== sessionIdOf(_dogId)) {
            return dog;
        }

        const current = dogSessionIds(dog);
        const updatedSessionIds = current.includes(id)
            ? current.filter((existing) => existing !== id)
            : [...current, id];

        return {
            ...dog,
            sessions: [{
                dog_id: dog._id,
                session_ids: updatedSessionIds
            }]
        };
    }));
  };

  const isDropIn = (dog: ISelectedDog, schedule: ISession[]) => {
    if (dog.sessions[0]?.session_ids) {
        if (dog.sessions[0]?.session_ids.length === schedule.length) {
            return false;
        }
    }

    return true;
  }

  // Clubs set a single "Capacity" field, which is stored as regular_capacity.
  // Drop-in capacity is often left at 0, so do not treat that as "full".
  const sessionAvailability = (session: ISession): "open" | "waitlist" | "full" => {
    if (course?.course_type !== Constants.course_type.drop_in) {
      if (!course?.has_entry_limit || (course.enrolled_count || 0) < (course.entry_limit || 0)) return "open";
      if ((course.waitlist_size || 0) > (course.waitlist_count || 0)) return "waitlist";
      return "full";
    }
    const capacity = session.regular_capacity;
    const enrolled = session.enrolled_count ?? session.regular_entries ?? 0;
    if (typeof capacity !== "number" || enrolled < capacity) return "open";
    if ((session.waitlist_size || 0) > (session.waitlist_entries || 0)) return "waitlist";
    return "full";
  };

  const sessionChip = (session: ISession) => {
    const state = sessionAvailability(session);
    if (state === "waitlist") return "Waitlist";
    if (state === "full") return "Full";
    const capacity = session.regular_capacity || 0;
    const enrolled = session.enrolled_count ?? session.regular_entries ?? 0;
    return `${Math.max(0, capacity - enrolled)} Spots Left`;
  };

  const isSessionFull = (session: ISession) => sessionAvailability(session) === "full";

  const selectAllSessions = (dog: ISelectedDog) => {
    return () => {
        setDogs(dogs.map(d => {
            if (d._id === dog._id) {
                return {
                    ...d,
                    sessions: [{
                        dog_id: d._id,
                        session_ids: scheduleSessionIds()
                    }]
                };
            }
            return d;
        }));
    }
  };

  useEffect(() => {
    if (loading) {
        const _cid = props.cid || cid;

        if (!_cid) {
            return;
        }

        if (user._id.length === 0) {
            toast.show({
                title: "Course Registration Error",
                content: "You must login to register to a course.",
                duration: 5000,
                type: "fail"
            });

            navigate("/login?r=" + location.pathname);
        } else {
            const queryParameters = new URLSearchParams(window.location.search);
            const uid = queryParameters.get("uid");
            const uemail = queryParameters.get("uemail");
            const uname = queryParameters.get("uname");
            const ujunior = queryParameters.get("junior");
            // const adminParam = queryParameters.get("admin");
            const claimed:boolean = queryParameters.get("claimed") === "true" ? true : false;

            apiService.getCourseSummaryForHandle(_cid).then(async (course) => {
                setCrumbs([
                    { label: "Courses", path: "/courses" },
                    { label: course.title, path: `/c/${course.handle}` },
                    { label: "Register" }
                ]);

                // Check if user has admin rights for this course
                let hasAdminAccess = false;
                
                // Club managers have admin access
                if (user.user_type === Constants.user_type.club_manager) {
                    hasAdminAccess = true;
                }
                // Course creator has admin access
                else if (course.created_by === user._id) {
                    hasAdminAccess = true;
                }
                // Dog owners with admin capacity for the club have admin access
                else if (user.user_type === Constants.user_type.owner) {
                    try {
                        const capacitiesResponse = await apiService.getUserCapacities();
                        hasAdminAccess = capacitiesResponse.capacities.some(capacity => 
                            capacity.club_id === course.club_id && capacity.capacities.includes(Constants.capacity_type.admin)
                        );

                        //setIsAdminUser(hasAdminAccess);
                    } catch (error) {
                        //
                    }
                }

                // If user has admin access but no query params, redirect back
                if (hasAdminAccess && !(uid && uemail && ujunior)) {
                    navigate("/c/" + course.handle);
                    return;
                }
                
                // If user has admin access and query params, set up registration on behalf
                if (hasAdminAccess && uid && uemail && ujunior) {
                        setIsClaimed(claimed);
                        setOnBehalf(uid);
                        setOnBehalfEmail(uemail);
                        setOnBehalfName(uname || uemail); // Use name if provided, otherwise fallback to email
                }

                // Check if user is banned from this club
                if (hasAdminAccess && uid) {
                    // Admin doing on-behalf registration - check if the on-behalf user is banned
                    try {
                        const banCheck = await apiService.checkIfSpecificUserIsBanned(course.club_id, uid);
                        isUserBannedRef.current = banCheck.is_banned;
                        setIsUserBanned(banCheck.is_banned);
                    } catch (error) {
                        console.error('Failed to check ban status for on-behalf user:', error);
                    }
                } else if (user.user_type === Constants.user_type.owner && !hasAdminAccess) {
                    // Regular user - check if they are banned
                    try {
                        const banCheck = await apiService.checkIfUserIsBanned(course.club_id);
                        isUserBannedRef.current = banCheck.is_banned;
                        setIsUserBanned(banCheck.is_banned);
                    } catch (error) {
                        console.error('Failed to check ban status:', error);
                        // Continue if check fails - the backend will still enforce bans
                    }
                }

                if (!hasAdminAccess && course.status === Constants.course_status.draft) {
                    toast.show({
                        title: "Course",
                        content: "This course hasn't been published yet",
                        duration: 10000,
                        type: "fail"
                    });
                    navigate("/");
                    return;
                }

                if (
                    !hasAdminAccess
                    && course.status === Constants.course_status.upcoming
                    && course.publish_at
                    && new Date(course.publish_at) > new Date()
                ) {
                    toast.show({
                        title: "Course",
                        content: "This course isn't live yet.",
                        duration: 10000,
                        type: "fail"
                    });
                    navigate("/c/" + course.handle);
                    return;
                }

                const hasWaiver = !!(course.waiver && course.waiver.trim() !== '');
                const hasClubPolicies = !!(course.club_policies && course.club_policies.trim() !== '');
                const waiverStep: IStep = { label: "Course Waiver" };
                const clubPoliciesStep: IStep = { label: "Club Policies" };
                const optionalSteps: IStep[] = [
                    ...(hasWaiver ? [waiverStep] : []),
                    ...(hasClubPolicies ? [clubPoliciesStep] : []),
                ];
                const paymentStep: IStep = { label: "Payment" };

                if (course.course_type && course.course_type === Constants.course_type.seminar) {
                    setSteps([{ label: "Select Sessions" }, ...optionalSteps, paymentStep]);
                    setDogs([]);
                } else if (course.course_type && course.course_type === Constants.course_type.full_course) {
                    setSteps([{ label: "Select Dogs" }, ...optionalSteps, paymentStep]);
                } else {
                    // Drop-in: default 3 steps + optional middle steps
                    if (hasWaiver || hasClubPolicies) {
                        setSteps([
                            { label: "Select Dogs" },
                            { label: "Select Sessions" },
                            ...optionalSteps,
                            paymentStep
                        ]);
                    }
                    // else: keep default 3 steps
                }

                setCourse(course);

                // Only fetch dogs if not a seminar
                if (course.course_type !== Constants.course_type.seminar) {
                    if (uid && uemail && ujunior) {
                        getDogs(course.club._id, uid);
                    } else {
                        getDogs();
                    }
                } else {
                    // For seminars, skip dog fetching and go straight to loading complete
                    setLoading(false);
                }
            });
        }
        setLoading(false);
    }
  }, [loading, props.cid, cid, getDogs, location.pathname, navigate, user._id, user.user_type]);

  useEffect(() => {
    // For seminars, we don't need dogs selected, but we do need sessions selected
    const hasSelection = course?.course_type === Constants.course_type.seminar
      ? true // Seminars always ready (user is the attendee)
      : dogs.filter(dog => dog.selected).length > 0;

    if (hasSelection && course) {
        const courseRegistrations = course.course_type === Constants.course_type.seminar
            ? [{ session_ids: seminarSessions }] // For seminars, use seminar sessions
            : dogs.filter(dog => dog.selected).map(dog => ({ 
                dog_id: dog._id, 
                session_ids: dog.sessions[0]?.session_ids || [] 
              }));

        apiService.getCourseRegistrationPaymentSummary(course._id as string, {
            course_registrations: courseRegistrations,
            course_waitlist_id: offerId || undefined,
            payment_method_type: Constants.payment_method_type.card,
            cheque_number: chequeNumber,
            registration_kind: onBehalf !== undefined && onBehalfEmail !== undefined ? Constants.registration_kind.club : Constants.registration_kind.owner,
            owner_id: onBehalf ? onBehalf : user._id,
            club_points_to_redeem: pointsToRedeem
        }).then((cart) => {
            setStripePromise(loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY || "", { stripeAccount: cart.account_number }));
            setCart(cart);
        }).catch((error: AxiosError) => {
            const errorMessage = (error.response?.data as any)?.message || "Something went wrong";
            const errorCode = (error.response?.data as any)?.eagerdog_error_code;

            // Known user-facing errors — show the message cleanly with no "View Details"
            const isUserFacingError =
                errorCode === "ERROR.DATA.RECORD_ALREADY_EXISTS" ||
                errorCode === "ERROR.DATA.MAX_RECORDS_REACHED";

            if (errorCode === "ERROR.DATA.RECORD_ALREADY_EXISTS") {
                setDogs(dogs.map(dog => ({ ...dog, selected: false })));

                // Only show toast once for this specific error to avoid spam
                if (lastErrorShown !== errorMessage) {
                    setLastErrorShown(errorMessage);
                    toast.show({
                        title: "Course Register Error",
                        content: errorMessage,
                        duration: 5000,
                        type: "fail"
                    });
                }
            } else {
                toast.show({
                    title: "Course Register Error",
                    content: errorMessage,
                    duration: 5000,
                    ...(isUserFacingError ? {} : { errorDetails: error }),
                    type: "fail"
                });
            }
        });
    } else {
        setCart(null);
    }
  }, [dogs, seminarSessions, chequeNumber, course, onBehalf, onBehalfEmail, user._id, pointsToRedeem]);

  const getWaiverSignoffLabel = (type: 'waiver' | 'policies') => {
    const subject = type === 'waiver' ? 'course waiver' : 'club policies';
    if (onBehalf && onBehalfName) {
      return `${onBehalfName} has read and agreed to the ${subject}.`;
    }
    return `I, ${user.display_name}, have read and agree to the ${subject}.`;
  };

  useEffect(() => {
    if (!course || offerApplied) return;
    const id = new URLSearchParams(window.location.search).get("waitlist");
    if (!id) return;
    if (course.course_type !== Constants.course_type.seminar && dogs.length === 0) return;
    apiService.getCourseWaitlist(course._id, id).then((row) => {
      if (row.status !== Constants.waitlist_status.awaiting_payment) {
        toast.show({ title: "Waitlist", content: "This offer is not ready to pay.", duration: 6000, type: "fail" });
        setOfferApplied(true);
        return;
      }
      setOfferId(row._id);
      const ids = (row.session_ids || []).map((sessionId) => String(sessionId));
      const sessionIds = ids.length ? ids : (row.session_id ? [String(row.session_id)] : (course.schedule || []).map((session) => String(session._id)));
      if (course.course_type === Constants.course_type.seminar) {
        setSeminarSessions(sessionIds);
      } else if (row.dog?.dog_id) {
        setDogs((current) => current.map((dog) => dog._id === String(row.dog?.dog_id) ? {
          ...dog,
          selected: true,
          sessions: [{ dog_id: dog._id, session_ids: sessionIds }],
        } : dog));
      }
      setCurrentStep(paymentStepIndex);
      setOfferApplied(true);
    }).catch(() => {
      toast.show({ title: "Waitlist", content: "This offer could not be opened.", duration: 6000, type: "fail" });
      setOfferApplied(true);
    });
  }, [course, offerApplied, paymentStepIndex, dogs.length]);

  // Scroll to top when step changes (e.g. clicking Next/Previous)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  useEffect(() => {
    if (!course) return;
    if (onBehalf !== undefined || onBehalfEmail !== undefined) return;
    if (currentStep !== paymentStepIndex) return;
    if (stripeIntent) return;

    const courseRegistrations = course.course_type === Constants.course_type.seminar
      ? [{ session_ids: seminarSessions }] // For seminars, use seminar sessions
      : dogs.filter(dog => dog.selected).map(dog => ({
          dog_id: dog._id,
          session_ids: dog.sessions[0].session_ids || []
        }));

    apiService.createCourseRegistrationPaymentIntent(course._id, {
        course_registrations: courseRegistrations,
        payment_method_type: Constants.payment_method_type.card,
        registration_kind: Constants.registration_kind.owner,
        owner_id: user._id,
        club_points_to_redeem: pointsToRedeem,
        course_waitlist_id: offerId || undefined,
    }).then((intent) => {
        setStripeIntent(intent);
    }).catch((err: any) => {
        console.error("[CourseRegister] Payment intent creation FAILED. pointsToRedeem:", pointsToRedeem, "error:", err?.response?.data || err?.message || err);
        toast.show({
            title: "Course Register Error",
            content: err?.response?.data?.message || "Registration could not be started.",
            duration: 6000,
            type: "fail",
        });
    });
  }, [
    currentStep,
    paymentStepIndex,
    course,
    dogs,
    seminarSessions,
    onBehalf,
    onBehalfEmail,
    stripeIntent,
    user._id,
    pointsToRedeem,
    offerId,
  ]);

  // Finalize a free (points-covered, $0) registration. Paid registrations are
  // completed through Stripe; free ones have no client_secret so they are
  // processed directly here.
  const pointsReminder = usePointsReminder(
    course?.club?._id,
    pointsReminderEnabled(user, onBehalf, onBehalfEmail)
  );
  const payRef = React.useRef<() => void>(() => {});

  const courseRedeemableAmount = () => {
    const details = cart?.details || [];
    const sessionTotal = details.reduce((total: number, detail: any) => {
      const sessions = detail.sessions || [];
      return total + sessions.reduce((sum: number, session: any) => sum + (Number(session.fee) || 0), 0);
    }, 0);
    if (sessionTotal > 0) return sessionTotal;
    return cart?.base_amount || cart?.total_amount || 0;
  };

  const applyCoursePoints = async (points: number) => {
    if (!course) return undefined;
    const courseRegistrations = course.course_type === Constants.course_type.seminar
      ? [{ session_ids: seminarSessions }]
      : dogs.filter(dog => dog.selected).map(dog => ({
          dog_id: dog._id,
          session_ids: dog.sessions[0]?.session_ids || []
        }));
    const summary = await apiService.getCourseRegistrationPaymentSummary(course._id as string, {
      course_registrations: courseRegistrations,
      payment_method_type: Constants.payment_method_type.card,
      registration_kind: Constants.registration_kind.owner,
      owner_id: user._id,
      club_points_to_redeem: points,
      course_waitlist_id: offerId || undefined,
      transaction_id: stripeIntent?.transaction_id,
      payment_intent_id: stripeIntent?.payment_intent_id,
    });
    setCart(summary);
    setPointsToRedeem(points);
    return summary.total_amount;
  };

  const askAboutPoints = (onContinue: () => void, onFree?: () => void) => {
    const redeemableAmount = courseRedeemableAmount();
    if (!pointsReminder.shouldAsk(redeemableAmount, pointsToRedeem > 0)) {
      onContinue();
      return;
    }
    pointsReminder.ask({
      redeemableAmount,
      onApply: applyCoursePoints,
      onContinue,
      onFree,
    });
  };

  const completeFreeRegistration = useCallback(() => {
    if (!course || !stripeIntent?.transaction_id) return;

    setCompletingFree(true);

    apiService.processCourseRegistrationTransaction(course._id, stripeIntent.transaction_id)
      .then(() => {
        window.location.href = Config.web.REACT_APP_WEB_URL + "/c/" + course.handle;
      })
      .catch((e: AxiosError) => {
        setCompletingFree(false);
        toast.show({
          title: "Course Registration",
          content: "Something went wrong completing your registration, please try again",
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      });
  }, [course, stripeIntent]);

  return (
    <div className={`${styles.CourseRegister} ${props.cid || cid ? styles.modaled : ""} ${styles[steps[currentStep].label]}`}>
      <Helmet>
        {course?.title && <title>{course?.title} Course Register - {course?.club_name ?? course?.club.club_name}</title>}
      </Helmet>
        <div className={styles.inner}>
            <Breadcrumb crumbs={crumbs} />
            <div className={styles.actionBar}>
                <div className={styles.steps}>
                    <RegisterSteps
                        currentStep={currentStep}
                        onStepClick={(step:number) => setCurrentStep(step)}
                        steps={steps}
                    />
                </div>
                <div className={styles.previous}>
                    {currentStep > 0 && <Button onClick={() => { setCurrentStep(currentStep - 1); }}>Previous</Button>}
                </div>
                {currentStep !== steps.length - 1 && <div className={styles.next}>
                    <Button onClick={() => {
                        if (currentStep !== steps.length - 1 && validStep(currentStep)) {
                            setCurrentStep(currentStep + 1);
                        }
                    }}>Next</Button>
                </div>}
            </div>
            {isOnBehalfOf() && <div className={styles.onBehalfMsg}>
                <div className={styles.msg}>You're registering on behalf of <span>{onBehalfEmail}</span>.
                {(!isClaimed) && <div className={styles.accountStatus}> This email doesn't have an active Eagerdog account, dogs on this account are managed by you.</div>}
                {(isClaimed) && <div className={styles.accountStatus}> This email has an active Eagerdog account, displaying dogs created by both the user and you.</div>}
                </div>
            </div>}
            {!loading && <div className={styles.contentWrap}>
                {/* Dog Selection Step - only for non-seminar courses at step 0 */}
                {currentStep === 0 && course?.course_type !== Constants.course_type.seminar && (
                    <div className={styles.stepContent}>
                        <p className={styles.hint}>Select all dogs you wish to register to the course. Select dogs you've already added to your account or add a new dog to your account by selecting "Add New Dog". </p>
                        <div className={getSessionCount() === getMaxDogCount() ? `${styles.courseInfo} ${styles.disabled}` : `${styles.courseInfo}`}>
                            <div className={styles.courseTitle}>
                                <span className={styles.title}><span>{course?.title}</span>{getSessionCount() === getMaxDogCount() && <span className={`${styles.status} ${styles.full}`}>Course Full</span>}</span>
                                <span className={styles.fee}>{formatter.format(course?.fee || 0)}</span>
                            </div>
                            <p className={styles.courseClub}>{course?.club_name ?? course?.club.club_name}</p>
                            {showSchedule && <div className={styles.schedule}>
                                {course?.schedule && course.schedule.length > 0 ? (
                                    course.schedule.map((schedule, index) => (
                                        <div key={index} className={styles.scheduleItem}>
                                            <div className={styles.sessionName}>Session {index + 1}</div>
                                            <div className={styles.sessionInfo}>
                                                <div className={styles.time}>
                                                <span className={styles.scheduleDate}>{moment(new Date(schedule.start_date)).format('ddd, MMM D, YYYY')}</span>
                                                <span className={styles.scheduleTime}>{moment(new Date(schedule.start_date)).format('h:mmA')} - {moment(new Date(schedule.end_date)).format('h:mmA')}</span>
                                            </div>
                                            <div className={styles.instructors}>
                                                <div className={styles.label}>Instructor</div>
                                                    {schedule.instructors && schedule.instructors.length > 0 ? (
                                                        schedule.instructors.map((instructor, idx) => (
                                                            <span key={idx} className={styles.instructor}>{instructor.first_name} {instructor.last_name}</span>
                                                        ))
                                                    ) : (
                                                        <span className={styles.noInstructors}>No instructors available</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <span className={styles.noSchedule}>No schedule available</span>
                                )}
                            </div>}
                            <div onClick={() => { setShowSchedule(!showSchedule); }} className={styles.scheduleToggle}><Icon type="alignleft" /><span>{showSchedule ? "Hide Schedule" : "Show Schedule"}</span></div>
                            {!loading && getSessionCount() !== getMaxDogCount() && <DogSelection
                                dogs={dogs}
                                onChange={(updatedDogs) => {
                                    // Check if user is banned when selecting a dog (use ref for immediate access)
                                    const isSelectingDog = updatedDogs.filter(d => d.selected).length > dogs.filter(d => d.selected).length;
                                    if (isSelectingDog && isUserBannedRef.current) {
                                        const isOnBehalfReg = onBehalf !== undefined && onBehalfEmail !== undefined;
                                        toast.show({
                                            title: "Registration",
                                            content: isOnBehalfReg
                                                ? "This user is not currently able to register for courses hosted by this club."
                                                : "This account is not currently able to register for courses hosted by this club. If you believe this is an error, please contact the course coordinator.",
                                            duration: 10000,
                                            type: "fail"
                                        });
                                        return;
                                    }

                                    // Vaccine certificate check
                                    if (isSelectingDog && course?.vaccine_requirements?.enabled) {
                                        const newlySelected = updatedDogs.filter(d =>
                                            d.selected && !dogs.find(old => old._id === d._id)?.selected
                                        );
                                        for (const dog of newlySelected) {
                                            const { passed, missingLabels } = checkDogVaccineRequirements(dog);
                                            if (!passed) {
                                                const missingStr = missingLabels.join(", ");
                                                const noteStr = course.vaccine_requirements.notes
                                                    ? ` Note: ${course.vaccine_requirements.notes}`
                                                    : "";
                                                if (course.vaccine_requirements.enforcement === "block") {
                                                    toast.show({
                                                        title: "Vaccine Requirements",
                                                        content: `${dog.call_name} is missing required vaccine certificate(s): ${missingStr}. Please update their health records before registering.${noteStr}`,
                                                        duration: 10000,
                                                        type: "fail"
                                                    });
                                                    // Revert to previous dogs state so DogSelection resets its internal state
                                                    setDogs([...dogs]);
                                                    return; // Block selection
                                                } else {
                                                    toast.show({
                                                        title: "Vaccine Requirements",
                                                        content: `Warning: ${dog.call_name} may be missing vaccine certificate(s): ${missingStr}. Please ensure their health records are up to date.${noteStr}`,
                                                        duration: 10000,
                                                        type: "fail"
                                                    });
                                                    // "warn" — allow selection to continue
                                                }
                                            }
                                        }
                                    }

                                    setDogs(updatedDogs.map((_dog: ISelectedDog, index: number) => { return {
                                        ..._dog,
                                        sessions: _dog.sessions.length === 0 ? [{
                                            dog_id: _dog._id,
                                            session_ids: scheduleSessionIds()
                                        }] : _dog.sessions
                                    }}));
                                }}
                                onAddDog={() => {
                                    toggle();
                                    setModal("addDog");
                                }}
                                onEditDog={() => {
                                    getDogs(course?.club._id, onBehalf || user._id);
                                }}
                                isAdmin={onBehalf !== undefined}
                            />}
                        </div>
                    </div>
                )}
                {/* Session Selection Step - for drop-in courses at step 1, or seminars at step 0 */}
                {((course && course.course_type === Constants.course_type.drop_in && currentStep === 1) || 
                  (course && course.course_type === Constants.course_type.seminar && currentStep === 0)) && (
                    <div className={styles.stepContent}>
                        <div className={styles.hint}>
                            {course.course_type === Constants.course_type.seminar 
                                ? "Select the sessions you'd like to attend."
                                : "Select the sessions you'd like to participate in. Depending on the kennel club, pricing could be based on a per-session basis."}
                        </div>
                        {course.course_type === Constants.course_type.seminar ? (
                            // Seminar session selection (no dogs)
                            <div className={styles.dogSessionWrap}>
                                <div className={styles.dogSessions}>
                                    <div className={styles.sessions}>
                                        <div className={styles.sessionTitle}>Sessions</div>
                                        {course?.schedule.map((session: ISession, sessionIndex: number) => {
                                            return <div key={session._id} className={styles.sessionWrap}>
                                                <div className={styles.checkWrap}>
                                                    <Checkbox
                                                        id={"seminar-"+session._id}
                                                        label={getSessionLabel(session)}
                                                        disabled={isSessionFull(session)}
                                                        value={seminarSessions.includes(sessionIdOf(session._id))}
                                                        onChange={(e) => {
                                                            const id = sessionIdOf(session._id);
                                                            if (seminarSessions.includes(id)) {
                                                                setSeminarSessions(seminarSessions.filter(existing => existing !== id));
                                                            } else {
                                                                setSeminarSessions([...seminarSessions, id]);
                                                            }
                                                        }}
                                                    />
                                                    <div className={styles.sessionDetails}>
                                                        <span className={styles.sessionName}>Session {sessionIndex + 1}</span>
                                                        <span className={styles.sessionCapacity}><Chip className={`${styles.capacity}`}>{sessionChip(session)}</Chip></span>
                                                    </div>
                                                </div>
                                            </div>;
                                        })}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // Dog-based session selection (drop-in courses)
                            dogs.filter((_dog: ISelectedDog) => _dog.selected === true).map((_mapDog: ISelectedDog, dogIndex: number) => {
                                return (<div className={styles.dogSessionWrap} key={_mapDog.call_name + dogIndex}>
                                    <div className={styles.dogSessions}>
                                        <div className={styles.dogName}>
                                            <DogPortrait
                                                dog={_mapDog}
                                            />
                                            {/*<div className={styles.regType}>{isDropIn(_mapDog, (course?.schedule ?? [])) ?
                                                <Chip className={isDropIn(_mapDog, (course?.schedule || [])) ? `${styles.capacity} ${styles.dropIn}` : `${styles.capacity}`}>Drop-In</Chip> : 
                                                <Chip className={isDropIn(_mapDog, (course?.schedule || [])) ? `${styles.capacity} ${styles.dropIn}` : `${styles.capacity}`}>Standard</Chip>}
                                            </div>*/}
                                        </div>
                                        <div className={styles.sessions}>
                                            <div className={styles.sessionTitle}>Sessions</div>
                                            {course?.schedule.map((session: ISession, sessionIndex: number) => {
                                                return <div key={session._id} className={styles.sessionWrap}>
                                                    <div className={styles.checkWrap}>
                                                        <Checkbox
                                                            id={_mapDog._id+"-"+session._id}
                                                            label={getSessionLabel(session)}
                                                            disabled={isSessionFull(session)}
                                                            value={dogHasSession(_mapDog, session._id)}
                                                            onChange={(e) => {
                                                                toggleSessionFromDog(_mapDog._id, session._id);
                                                            }}
                                                        />
                                                        <div className={styles.sessionDetails}>
                                                            <span className={styles.sessionName}>Session {sessionIndex + 1}</span>
                                                            <span className={styles.sessionCapacity}><Chip className={`${styles.capacity}`}>{sessionChip(session)}</Chip></span>
                                                        </div>
                                                    </div>
                                                </div>;
                                            })}
                                        </div>
                                    </div>
                                    {false /*isDropIn(_mapDog, (course?.schedule || []))*/ && <div className={styles.dropInInfo}>{_mapDog.call_name}'s registration is now a drop-in registration. Drop-in registrations have different availability from standard registrations and your registration has been adjusted accordingly. <span className={styles.switchRegistration} onClick={selectAllSessions(_mapDog)}>Switch back to a standard registration</span>.</div>}
                                </div>);
                            })
                        )}
                    </div>
                )}
                {/* Course Waiver Step */}
                {waiverStepIndex !== -1 && currentStep === waiverStepIndex && course && (
                    <div className={styles.stepContent}>
                        <div className={styles.hint}>Please read the following waiver carefully before proceeding.</div>
                        <div
                            className={styles.waiverText}
                            dangerouslySetInnerHTML={{ __html: course.waiver! }}
                        />
                        <div className={styles.waiverAgree}>
                            <Checkbox
                                id="courseWaiverAgree"
                                value={agreeToWaiver}
                                onChange={() => setAgreeToWaiver(!agreeToWaiver)}
                                label={getWaiverSignoffLabel('waiver')}
                            />
                        </div>
                    </div>
                )}
                {/* Club Policies Step */}
                {clubPoliciesStepIndex !== -1 && currentStep === clubPoliciesStepIndex && course && (
                    <div className={styles.stepContent}>
                        <div className={styles.hint}>Please read the following club policies carefully before proceeding.</div>
                        <div
                            className={styles.waiverText}
                            dangerouslySetInnerHTML={{ __html: course.club_policies! }}
                        />
                        <div className={styles.waiverAgree}>
                            <Checkbox
                                id="courseClubPoliciesAgree"
                                value={agreeToClubPolicies}
                                onChange={() => setAgreeToClubPolicies(!agreeToClubPolicies)}
                                label={getWaiverSignoffLabel('policies')}
                            />
                        </div>
                    </div>
                )}
                {/* Payment Step */}
                {currentStep === paymentStepIndex && (
                    <div className={styles.stepContent}>
                        <div className={styles.CheckoutWrap}>
                            <div className={styles.title}>Dog Owner Information</div>
                            <UserAddress
                                user={(() => {
                                    // If any dog has owner_info, use that instead of user info
                                    const dogWithOwnerInfo = dogs.find(d => d.owner_info);
                                    if (dogWithOwnerInfo?.owner_info) {
                                        const ownerInfo = dogWithOwnerInfo.owner_info;
                                        // Create a user-like object from dog owner_info
                                        return {
                                            ...user,
                                            first_name: ownerInfo.first_name,
                                            last_name: ownerInfo.last_name,
                                            display_name: `${ownerInfo.first_name} ${ownerInfo.last_name}`,
                                            address: ownerInfo.address,
                                            phone: ownerInfo.phone,
                                            email: ownerInfo.email,
                                        };
                                    }
                                    return user;
                                })()}
                                onChange={(address: IAddress, phone: string) => {
                                    setUserAddress(address);
                                    setUserPhone(phone);
                                }
                            } />
                            {onBehalf === undefined && onBehalfEmail === undefined && !!stripeIntent?.client_secret && stripePromise !== undefined && <div className={styles.Payment}>
                                <Elements key={stripeIntent?.payment_intent_id} stripe={stripePromise} options={{clientSecret: stripeIntent?.client_secret}}>
                                    <Checkout
                                        redirect={Config.web.REACT_APP_WEB_URL + "/c/" + course?.handle}
                                        userAddress={userAddress}
                                        userPhone={userPhone}
                                        bindPay={(pay) => { payRef.current = pay; }}
                                        pointsGuard={() => {
                                          if (!pointsReminder.shouldAsk(courseRedeemableAmount(), pointsToRedeem > 0)) {
                                            return false;
                                          }
                                          pointsReminder.ask({
                                            redeemableAmount: courseRedeemableAmount(),
                                            onApply: applyCoursePoints,
                                            onContinue: () => payRef.current(),
                                            onFree: () => completeFreeRegistration(),
                                          });
                                          return true;
                                        }}
                                    />
                                </Elements>
                            </div>}
                            {onBehalf === undefined && onBehalfEmail === undefined && stripeIntent?.waitlisted && !stripeIntent.transaction_id && <div className={styles.Payment}>
                                <p>You're on the waitlist. You have not been charged.</p>
                                <div className={styles.actions}>
                                    <Button onClick={() => navigate("/c/" + course?.handle)}>Back to {course?.course_type === Constants.course_type.seminar ? "Seminar" : course?.course_type === Constants.course_type.drop_in ? "Class" : "Course"}</Button>
                                </div>
                            </div>}
                            {onBehalf === undefined && onBehalfEmail === undefined && stripeIntent?.waitlisted && !!stripeIntent.client_secret && <p>Full sessions were added to the waitlist and are not included in this charge.</p>}
                            {onBehalf === undefined && onBehalfEmail === undefined && !!stripeIntent && !stripeIntent.client_secret && !stripeIntent.waitlisted && <div className={styles.Payment}>
                                <div className={styles.actions}>
                                    <Button isLoading={completingFree} disabled={completingFree} onClick={() => askAboutPoints(() => completeFreeRegistration())}>Complete Registration</Button>
                                </div>
                            </div>}
                            {onBehalf !== undefined && onBehalfEmail !== undefined && <div className={styles.Payment}>
                                <Dropdown value={onBehalfPaymentMethod} label={"Payment Method"} onChange={(e, value: IOption) => { setOnBehalfPaymentMethod(value); }} options={onBehalfPaymentMethods} placeholder="" />
                                {onBehalfPaymentMethod.id === Constants.payment_method_type.cheque &&
                                    <Input required label="Cheque Number" type="text" onChange={(e) => { e.stopPropagation(); setChequeNumber(e.target.value); }} defaultValue={chequeNumber} />
                                }
                                <div className={styles.actions}>
                                    <Button isLoading={false} onClick={() => { registerOnBehalf(); }}>Register User</Button>
                                </div>
                            </div>}
                        </div>
                    </div>
                )}
                {course && <CourseCart
                    className={styles.cart}
                    innerClassName={styles.cartInner}
                    course={course}
                    cart={cart}
                    onBehalfEmail={onBehalfEmail}
                    onBehalfName={onBehalfName}
                    onBehalfId={onBehalf}
                    showPoints={currentStep === paymentStepIndex}
                    onUpdatePoints={(points: number) => {
                        setStripeIntent(null);
                        setPointsToRedeem(points);
                    }}
                />}
            </div>}
            {currentStep !== (steps.length - 1) && <div className={`${styles.actionBar} ${styles.bottom}`}>
                <div className={styles.previous}>
                    {currentStep > 0 && <Button onClick={() => { setCurrentStep(currentStep - 1); }}>Previous</Button>}
                </div>
                {currentStep !== steps.length - 1 && <div className={styles.next}>
                    <Button onClick={() => {
                        if (currentStep !== steps.length - 1 && validStep(currentStep)) {
                            setCurrentStep(currentStep + 1);
                        }
                    }}>Next</Button>
                </div>}
            </div>}
        </div>
        {pointsReminder.modal}
        <Modal
            className={"dogForm"}
            isShown={isShown}
            hide={() => { toggle(); setModal(undefined); }}
            modalContent={getModalContent()}
        />
    </div>
  );
};

export default CourseRegister;
